import axios from 'axios';
import intl from 'src/locales';
import { isLanguageCN } from 'src/utils/env';
import { MessageError } from 'src/components/Message/index';
import { getAuthorizationData, getGuidKey } from 'src/utils/localStorage';

/**
  709: 检查token无效
  1114: 还未注册
  1113: 试用申请 - 审批拒绝
  1117: 试用申请 - 账号被禁用
  1116: 未通过申请
  1119: 已注册未申请试用
  1200：没有支付
**/
const ignoreUrl = [
  '/v1/base/cos/signature', // 获取cos临时签名
  '/v1/im/msg/predictionQuestion' // 预测问题
]; // 不走公共报错url
const loginIgnoreUrl = [
  '/v1/msg/tts', // 语音
  '/v1/im/msg/send', // 消息发送
  '/v1/msg/recommend', // 评论
  '/v1/im/session/set', // 新会话
  '/v1/im/pull/session', // 会话列表
  '/v1/im/pull/msg/history', // 历史消息
  '/v1/base/cos/signature', // 获取cos临时签名
  '/v1/user/isLogin' // 获取申请状态信息
]; // 该配置中接口触发 loginIgnoreCodes 中code错误时，不报错，走登录/申请逻辑
export const loginIgnoreCodes = ['709', '1114']; // 弹登录弹窗
export const applyIgnoreCodes = ['1116', '1119', '1113', '1117']; // 弹申请弹窗
export const payIgnoreCodes = ['1200']; // 弹支付弹窗

// 错误code
const errorCode: Record<number | string, string> = {
  401: intl.formatMessage({ id: 'request_authentication_failed' }),
  403: intl.formatMessage({ id: 'request_no_permission' }),
  404: intl.formatMessage({ id: 'request_access_resource_does_not_exist' }),
  0: intl.formatMessage({ id: 'request_unknown_system_error' })
};

axios.defaults.headers['Content-Type'] = 'application/json;charset=utf-8';
axios.defaults.headers['Accept-Language'] = isLanguageCN() ? 'zh-CN;q=0.9,en;q=0.8' : 'zh-CN;q=0.8,en;q=0.9';

const service = axios.create({
  baseURL: process.env.REACT_APP_BASE_SERVER,
  timeout: 60000,
  withCredentials: false // 禁用 Cookie 等信息
});

// 添加请求拦截器统一向发送的请求添加token
service.interceptors.request.use(
  (config) => {
    const searchParams = new URLSearchParams(window.location.search);
    // 智能体id
    const botId = searchParams.get('bot_id');
    // 样式id
    const styleId = searchParams.get('style_id');
    // 渠道id
    const channelId = searchParams.get('channel_id');
    // 访问模式 - 正常，预览
    const accessMode = searchParams.get('access_mode') ? searchParams.get('access_mode') : 'normal';
    let Authorization;
    const authData = getAuthorizationData();
    // token
    if (accessMode === 'preview') { // 预览token
      Authorization = authData?.previewToken;
    } else if (authData?.isAnonymous === '1') { // 匿名token
      Authorization = authData?.anonymousToken;
    } else { // 登录token
      Authorization = authData?.token;
    }
    config.headers['X-Bot-Id'] = botId;
    config.headers['X-Style-Id'] = styleId;
    config.headers['X-Access-Mode'] = accessMode;
    config.headers['X-Channel-Id'] = channelId;
    config.headers['X-Guid'] = getGuidKey();
    if (Authorization) {
      config.headers.Authorization = Authorization;
    }
    return config;
  }, error => {
    Promise.reject(error);
  }
);

service.interceptors.response.use(
  async (res) => {
    // 未设置状态码则默认成功状态
    const code = res.data.code || 200;
    const url = res?.config?.url ?? '';
    // 获取错误信息
    const msg = res.data.message || errorCode[code] || errorCode[0];
    if (ignoreUrl?.includes(url) && code !== 200 && code !== '1') {
      return await Promise.reject(res.data);
    } else if (loginIgnoreUrl?.includes(url) && (loginIgnoreCodes?.includes(code) || applyIgnoreCodes?.includes(code) || payIgnoreCodes?.includes(code))) { // 如果是忽略的错误码，直接返回 msg 异常
      return await Promise.reject(res?.data || { code, msg });
    } else if (code !== 200 && code !== '1') {
      MessageError({ content: msg });
      return await Promise.reject(res.data);
    } else {
      return res.data;
    }
  }, async error => {
    const { message = intl.formatMessage({ id: 'request_system_request_error' }), config } = error;
    if (ignoreUrl?.includes(config?.url)) {
      return await Promise.reject(error);
    } else {
      MessageError({ content: message });
      return await Promise.reject(error);
    }
  }
);

export default service;
